import type React from "react";
import { useCallback, useState } from "react";

import { produce } from "immer";
import type * as Papa from "papaparse";
import styled from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import { OnOffSwitch, StructureModal } from "@similarweb/ui-components";
import { Text, Stack } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { allTrackers } from "app/services/track/track";

import { ColumnMappingItem } from "./ColumnMappingItem";
import { findMostSimilarColumnName, getExcelColumnNames } from "./columnMappingUtils";

type MappingConfig = {
    keyword: number;
    tags: number;
    targetUrl: number;
};

export type MappingData = {
    fileName: string;
    id: string;
    mappingConfig: MappingConfig;
    parsedData: string[][];
    startRow: number;
};

interface IColumnMappingModalProps {
    fileName: string;
    parseResult: Papa.ParseResult<unknown>;
    onConfirm: (mappingData: MappingData) => void;
    onCancel?: () => void;
}

const Grid = styled.div`
    display: grid;
    grid-template-columns: auto auto 1fr 1fr;
    align-items: center;
    gap: 12px;
`;

const StyledModal = styled(StructureModal)`
    div[data-automation="modal-footer"] {
        border-top: 1px solid ${colorsPalettes.carbon[100]};
        padding: 16px;
    }
`;

export const ColumnMappingModal = ({ parseResult, onConfirm, onCancel, fileName }: IColumnMappingModalProps) => {
    const [hasHeaders, setHasHeaders] = useState(true);
    const [firstRow] = parseResult.data as string[][];
    const options = hasHeaders ? firstRow : getExcelColumnNames(firstRow);

    const [mappingConfig, setMappingConfig] = useState({
        keyword: findMostSimilarColumnName(options, "keyword"),
        tags: findMostSimilarColumnName(options, "tags"),
        targetUrl: findMostSimilarColumnName(options, "target url"),
    });

    const handleColumnSelectChange = useCallback((newIndex: number, key: keyof typeof mappingConfig) => {
        setMappingConfig((prev) =>
            produce(prev, (draft) => {
                draft[key] = newIndex;
            }),
        );
    }, []);

    const handleConfirm = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            const id = fileName;
            const startRow = hasHeaders ? 1 : 0;
            const data = parseResult.data as string[][];
            const mappingData: MappingData = {
                fileName,
                id,
                mappingConfig,
                parsedData: data,
                startRow,
            };

            onConfirm(mappingData);
        },
        [fileName, hasHeaders, mappingConfig, onConfirm, parseResult.data],
    );

    const handleCancel = useCallback(
        (e?: React.MouseEvent | React.KeyboardEvent) => {
            e?.preventDefault();
            allTrackers.dispatchTrackEvent("Button", "click", "Import CSV/Cancel mapping");
            onCancel?.();
        },
        [onCancel],
    );

    const handleHeaderToggle = useCallback(() => {
        setHasHeaders((prev) => {
            allTrackers.dispatchTrackEvent("Button", "click", `Contains Header/${!prev ? "Yes" : "No"}`);
            return !prev;
        });
    }, []);

    const canConfirm =
        mappingConfig.keyword !== -1 &&
        (parseResult.data as string[][])[hasHeaders ? 1 : 0][mappingConfig.keyword].length > 0;

    return (
        <StyledModal
            title={""}
            isOpen={true}
            mainButton={{
                label: translate("campaigns.keywordsStep.columnMapping.confirmButton"),
                onClick: handleConfirm,
                type: "primary",
                isDisabled: !canConfirm,
                dataAutomation: "confirm-mapping-button",
                tooltipEnabled: false,
            }}
            customStyles={{ content: { width: "700px", height: "550px" } }}
            additionalButton={{
                label: translate("campaigns.keywordsStep.columnMapping.cancelButton"),
                onClick: handleCancel,
                dataAutomation: "cancel-mapping-button",
                type: "flat",
                tooltipEnabled: false,
            }}
            closeIconConfig={{
                show: true,
                onClick: handleCancel,
            }}
            showHeaderDivider={false}
            contentPadding="0px 24px"
        >
            <Stack
                height="100%"
                padding="0 16px 24px 16px"
                justifyContent="space-between"
            >
                <Stack gap="40px">
                    <Stack gap="8px">
                        <Text fontVariant="h4">{translate("campaigns.keywordsStep.columnMapping.title")}</Text>
                        <Text fontVariant="report">
                            {translate("campaigns.keywordsStep.columnMapping.description")}
                        </Text>
                    </Stack>

                    <Grid>
                        <Text
                            fontVariant="caption"
                            color={colorsPalettes.carbon[300]}
                        >
                            {translate("campaigns.keywordsStep.columnMapping.campaignProperties")}
                        </Text>
                        <span /> {/* Empty column for alignment */}
                        <Text
                            fontVariant="caption"
                            color={colorsPalettes.carbon[300]}
                        >
                            {translate("campaigns.keywordsStep.columnMapping.csvColumn")}
                        </Text>
                        <Text
                            fontVariant="caption"
                            color={colorsPalettes.carbon[300]}
                        >
                            {translate("campaigns.keywordsStep.columnMapping.preview")}
                        </Text>
                        <ColumnMappingItem
                            label={translate("campaigns.keywordsStep.columnMapping.keywordLabel")}
                            options={options}
                            selectedIndex={mappingConfig.keyword}
                            disabledIndexes={[mappingConfig.tags, mappingConfig.targetUrl]}
                            onChange={(newIndex) => handleColumnSelectChange(newIndex, "keyword")}
                            previewData={parseResult.data as string[][]}
                            hasHeaders={hasHeaders}
                        />
                        <ColumnMappingItem
                            label={translate("campaigns.keywordsStep.columnMapping.tagsLabel")}
                            options={options}
                            selectedIndex={mappingConfig.tags}
                            disabledIndexes={[mappingConfig.keyword, mappingConfig.targetUrl]}
                            onChange={(newIndex) => handleColumnSelectChange(newIndex, "tags")}
                            previewData={parseResult.data as string[][]}
                            hasHeaders={hasHeaders}
                        />
                        <ColumnMappingItem
                            label={translate("campaigns.keywordsStep.columnMapping.targetUrlLabel")}
                            options={options}
                            selectedIndex={mappingConfig.targetUrl}
                            disabledIndexes={[mappingConfig.keyword, mappingConfig.tags]}
                            onChange={(newIndex) => handleColumnSelectChange(newIndex, "targetUrl")}
                            previewData={parseResult.data as string[][]}
                            hasHeaders={hasHeaders}
                        />
                    </Grid>
                </Stack>

                <OnOffSwitch
                    onLabel={translate("campaigns.keywordsStep.columnMapping.firstRowContainsHeaders")}
                    isSelected={hasHeaders}
                    onClick={handleHeaderToggle}
                />
            </Stack>
        </StyledModal>
    );
};
